import React, { useState, useEffect } from 'react';
import { apiClient } from '../../components/api/ApiClient';
import JalaliDatePicker from '../../components/common/JalaliDatePicker';

const getPersonType = (type) => {
    switch (type) {
        case 1: return 'حقیقی';
        case 2: return 'حقوقی';
        case 3: return 'مشارکت مدنی';
        case 4: return 'اتباع غیر ایرانی';
        default: return 'نامشخص';
    }
};

const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
    });
};

function BranchModal({ isVisible, onClose, initialData, onSave }) {
    const [step, setStep] = useState('form');
    const [formData, setFormData] = useState({
        companyName: '',
        name: initialData?.name || '',
        code: initialData?.code || '',
        economicCode: initialData?.company?.economicCode || '',
        taxMemoryId: initialData?.taxMemoryId || '',
        registrationDate: '',
        provinceCode: initialData?.province || '',
        cityCode: initialData?.city || '',
        street: initialData?.street || '',
        pelak: initialData?.pelak || '',
        postalCode: initialData?.postalCode || '',
        acceptContract: initialData?.acceptContract || false,
        lastInvoiceDate: initialData?.lastInvoiceDate || '',
        lastInvoiceNum: initialData?.lastInvoiceNum || '',
    });
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [rulesAccepted, setRulesAccepted] = useState(false);
    const [tempRulesAccepted, setTempRulesAccepted] = useState(false);

    useEffect(() => {
        if (!isVisible) return;
        setStep('form');
        setTempRulesAccepted(false);

        async function fetchData() {
            setLoading(true);
            try {
                const companyRes = await apiClient('/companies/api/');
                if (companyRes.companies && companyRes.companies.length > 0) {
                    const company = companyRes.companies[0];
                    setCompanyInfo(company);
                    setFormData(prev => ({
                        ...prev,
                        companyName: `${company.personType === 2 ? 'شرکت' : 'شخص'} (${getPersonType(company.personType)}) - ${company.name || ''}`,
                    }));
                }

                const provincesRes = await apiClient('/account/api/provinces');
                setProvinces(provincesRes);

            } catch (err) {
                console.error("Error fetching initial data:", err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [isVisible, initialData]);

    useEffect(() => {
        if (!isVisible || !formData.provinceCode) {
            setCities([]);
            return;
        }

        async function fetchCities() {
            try {
                const citiesRes = await apiClient(`/account/api/cities/?province=${formData.provinceCode}`);
                setCities(citiesRes);
            } catch (err) {
                console.error("Error fetching cities:", err);
            }
        }
        fetchCities();
    }, [formData.provinceCode, isVisible]);

    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                name: initialData.name || '',
                code: initialData.code || '',
                economicCode: initialData.company?.economicCode || '',
                taxMemoryId: initialData.taxMemoryId || '',
                registrationDate: initialData.registrationDate || '',
                provinceCode: initialData.province || '',
                cityCode: initialData.city || '',
                street: initialData.street || '',
                pelak: initialData.pelak || '',
                postalCode: initialData.postalCode || '',
                acceptContract: initialData.acceptContract || false,
                lastInvoiceDate: initialData.lastInvoiceDate || '',
                lastInvoiceNum: initialData.lastInvoiceNum || '',
            }));
            setRulesAccepted(initialData.acceptContract || false);
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name) => (dateString) => {
        setFormData(prev => ({ ...prev, [name]: dateString }));
    };

    const handleProvinceChange = (e) => {
        setFormData(prev => ({ ...prev, provinceCode: e.target.value, cityCode: '' }));
    };

    const [certificateFileName, setCertificateFileName] = useState('');
    const [privateKeyFileName, setPrivateKeyFileName] = useState('');
    const [certificateContent, setCertificateContent] = useState('');
    const [privateKeyContent, setPrivateKeyContent] = useState('');
    const [dragOverCert, setDragOverCert] = useState(false);
    const [dragOverKey, setDragOverKey] = useState(false);

    const handleCertificateFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCertificateFileName(file.name);
            const reader = new FileReader();
            reader.onload = (event) => {
                setCertificateContent(event.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handlePrivateKeyFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPrivateKeyFileName(file.name);
            const reader = new FileReader();
            reader.onload = (event) => {
                setPrivateKeyContent(event.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handleCertificateDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverCert(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            setCertificateFileName(file.name);
            const reader = new FileReader();
            reader.onload = (event) => {
                setCertificateContent(event.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handlePrivateKeyDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverKey(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            setPrivateKeyFileName(file.name);
            const reader = new FileReader();
            reader.onload = (event) => {
                setPrivateKeyContent(event.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handleDragOver = (type) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'certificate') setDragOverCert(true);
        else setDragOverKey(true);
    };

    const handleDragLeave = (type) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'certificate') setDragOverCert(false);
        else setDragOverKey(false);
    };

    const handleRulesAccept = () => {
        setFormData(prev => ({ 
            ...prev, 
            acceptContract: true
        }));
        setStep('form');
        setRulesAccepted(true);
    };

    const isEditing = !!initialData;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            name: formData.name,
            code: formData.code || '',
            taxMemoryId: formData.taxMemoryId || null,
            company: companyInfo?.id || null,
            acceptContract: rulesAccepted,
            certificate: certificateContent || '',
            privateKey: privateKeyContent || '',
            lastInvoiceNum: formData.lastInvoiceNum || null,
            lastInvoiceDate: formData.lastInvoiceDate || null,
            province: formData.provinceCode || null,
            city: formData.cityCode || null,
            street: formData.street || '',
            pelak: formData.pelak || '',
            postalCode: formData.postalCode || '',
        };

        try {
            if (isEditing) {
                await apiClient('/companies/api/branches/', {
                    method: 'PATCH',
                    data: { ...payload, branchId: `${initialData.id}` }
                });
            } else {
                await apiClient('/companies/api/branches/', {
                    method: 'POST',
                    data: payload
                });
            }
            onSave(formData);
            onClose();
        } catch (err) {
            console.error("Error saving branch:", err);
            alert("خطا در ذخیره اطلاعات: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                {step === 'form' && (
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{isEditing ? 'ویرایش شعبه' : 'اضافه کردن شعبه جدید'}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {loading ? (
                                <div className="text-center text-dark">در حال بارگذاری اطلاعات...</div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">شرکت</label>
                                            <input type="text" className="form-control" value={formData.companyName} disabled />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">نام شعبه <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">کد شعبه</label>
                                            <input type="text" className="form-control" name="code" value={formData.code} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">کد اقتصادی</label>
                                            <input type="text" className="form-control" name="economicCode" value={formData.economicCode} disabled />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">شناسه حافظه مالیاتی <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="taxMemoryId" value={formData.taxMemoryId} onChange={handleChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">استان <span className="text-danger">*</span></label>
                                            <select className="form-select" name="provinceCode" value={formData.provinceCode} onChange={handleProvinceChange} required>
                                                <option value="">انتخاب کنید...</option>
                                                {provinces.map(p => (
                                                    <option key={p.code} value={p.code}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">شهر <span className="text-danger">*</span></label>
                                            <select className="form-select" name="cityCode" value={formData.cityCode} onChange={handleChange} required disabled={!formData.provinceCode}>
                                                <option value="">انتخاب کنید...</option>
                                                {cities.map(c => (
                                                    <option key={c.code} value={c.code}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">خیابان</label>
                                            <input type="text" className="form-control" name="street" value={formData.street} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">پلاک</label>
                                            <input type="text" className="form-control" name="pelak" value={formData.pelak} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">کد پستی</label>
                                            <input type="text" className="form-control" name="postalCode" value={formData.postalCode} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">تاریخ و زمان آخرین صورتحساب</label>
                                            <JalaliDatePicker
                                                value={formData.lastInvoiceDate}
                                                onChange={handleDateChange('lastInvoiceDate')}
                                                placeholder="تاریخ و زمان آخرین صورتحساب"
                                                showTime={true}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">شماره آخرین صورتحساب</label>
                                            <input type="text" className="form-control" name="lastInvoiceNum" value={formData.lastInvoiceNum} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-12">
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" checked={rulesAccepted} disabled id="rulesCheck" />
                                                <label className="form-check-label text-dark" htmlFor="rulesCheck">
                                                    پذیرفتن قوانین و مقررات مودیان (وضعیت فعلی: {rulesAccepted ? 'پذیرفته شده' : 'پذیرفته نشده'})
                                                </label>
                                                <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setStep('rules_summary')}>
                                                    مشاهده و پذیرش قوانین
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {(isEditing && !initialData?.hasKeys) && (
                                            <div className="col-md-12">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div 
                                                            className={`border rounded p-3 text-center ${dragOverCert ? 'bg-light border-primary' : 'border-dashed'}`}
                                                            style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                                            onDragOver={handleDragOver('certificate')}
                                                            onDragLeave={handleDragLeave('certificate')}
                                                            onDrop={handleCertificateDrop}
                                                        >
                                                            <div className="mb-2">
                                                                <i className="feather-file display-4 text-muted"></i>
                                                            </div>
                                                            <div className="mb-2 text-muted small">
                                                                {certificateFileName || 'فایل سرتیفیکیت را در اینجا رها کنید یا انتخاب کنید'}
                                                            </div>
                                                            <input 
                                                                type="file" 
                                                                className="d-none" 
                                                                id="certFile" 
                                                                onChange={handleCertificateFileChange}
                                                                accept=".crt,.cer,.pem"
                                                            />
                                                            <label htmlFor="certFile" className="btn btn-primary btn-sm">
                                                                انتخاب فایل
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div 
                                                            className={`border rounded p-3 text-center ${dragOverKey ? 'bg-light border-primary' : 'border-dashed'}`}
                                                            style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                                            onDragOver={handleDragOver('privateKey')}
                                                            onDragLeave={handleDragLeave('privateKey')}
                                                            onDrop={handlePrivateKeyDrop}
                                                        >
                                                            <div className="mb-2">
                                                                <i className="feather-key display-4 text-muted"></i>
                                                            </div>
                                                            <div className="mb-2 text-muted small">
                                                                {privateKeyFileName || 'فایل کلید خصوصی را در اینجا رها کنید یا انتخاب کنید'}
                                                            </div>
                                                            <input 
                                                                type="file" 
                                                                className="d-none" 
                                                                id="keyFile" 
                                                                onChange={handlePrivateKeyFileChange}
                                                                accept=".key,.pem"
                                                            />
                                                            <label htmlFor="keyFile" className="btn btn-primary btn-sm">
                                                                انتخاب فایل
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer mt-4">
                                        <button type="button" className="btn btn-secondary" onClick={onClose}>انصراف</button>
                                        <button type="submit" className="btn btn-primary" disabled={!rulesAccepted || loading}>{isEditing ? 'ذخیره تغییرات' : 'ثبت شعبه'}</button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                )}

                {step === 'rules_summary' && (
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">خلاصه قوانین و مقررات مودیان</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body p-0">
                             <div className="alert alert-info border-0 rounded-0 mb-0">
                                <p className="mb-0 fs-12">لطفا قبل از فعالسازی، قوانین و مقررات استفاده از سامانه را مطالعه نمایید.</p>
                            </div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <img
                                    src="/assets/images/terms-and-rules/rules.jpg"
                                    alt="قوانین و مقررات مودیان"
                                    className="img-fluid d-block w-100"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                             <button type="button" className="btn btn-secondary" onClick={() => setStep('form')}>بازگشت</button>
                             <button type="button" className="btn btn-primary" onClick={() => setStep('rules_full')}>مشاهده قوانین</button>
                        </div>
                    </div>
                )}

                {step === 'rules_full' && (
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">متن کامل قوانین و مقررات مودیان</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body p-0">
                             <div className="alert alert-info border-0 rounded-0 mb-0">
                                <p className="mb-0 fs-12">لطفا قوانین کامل را مطالعه کرده و تایید نمایید.</p>
                            </div>
                            <div style={{ padding: '20px', lineHeight: '2' }}>
                                <h6 className="fw-bold mb-3">قوانین :</h6>
                                <p>
                                    از آنجا که ارتباط با کارپوشه مالیاتی و اتصال به سامانه مودیان ، از طریق فایل کلید خصوصی و فایل گواهی امضا میسر است؛ برای ارسال صورتحساب به سامانه مودیان دو امکان در نظر گرفته شده است :
                                </p>
                                
                                <h6 className="fw-bold mt-4 mb-2">۱- بارگزاری فایل ها در قسمت مشخص شده در هنگام ارسال صورتحساب :</h6>
                                <p>
                                    در این حالت فایل کلید خصوصی و فایل گواهی امضا نزد کاربر نگهداری شده و در هر مرحله که نیاز به ارتباط با سامانه مودیان است ضروریست کاربر فایلهای فوق را بارگزاری نماید. در چنین حالتی ارائه خدمات مرتبط با سامانه مودیان شامل ارسال صورتحسابها ، دریافت پاسخ از سامانه مودیان، استعلامات مبتنی بر سامانه مودیان تماما بعهده و توسط کاربر انجام می شود.
                                </p>
                                
                                <h6 className="fw-bold mt-4 mb-2">۲- بارگزاری فایل ها روی سرور شرکت :</h6>
                                <p>
                                    در این حالت نیاز است تا یک بار فایل کلید خصوصی و فایل گواهی امضا روی سرور مرکزی شرکت فراز اندیشه آرسین توسط کاربر بارگزاری شده تا عملیات ارتباط با سامانه مودیان بصورت خودکار انجام شود. در چنین حالتی خدمات زیر بصورت خودکار انجام می شود:
                                </p>
                                <ul className="mt-2">
                                    <li>ارسال صورتحساب به سامانه مودیان توسط کاربر بدون درخواست مجدد فایلها</li>
                                    <li>استعلام خودکار وضعیت صورتحساب در سامانه مودیان</li>
                                    <li>ارسال خودکار صورتحسابها به سامانه مودیان</li>
                                    <li>و سایر مواردی که بمنظور ارائه خدمات مطلوبتر به کاربران ، در آینده به امکانات نرم افزار اضافه می شود.</li>
                                </ul>
                                
                                <div className="alert alert-warning mt-4">
                                    <h6 className="fw-bold"><i className="feather-alert-triangle me-2"></i>نکته بسیار مهم :</h6>
                                    <p className="mb-0">
                                        با توجه به اینکه اطلاعات کاربر روی سرورهای معتبر و ایمن نگهداری می شود مسئولیت حفظ و حراست از فایلهای کلید خصوصی و گواهی امضا بعهده کاربر بوده و هیچ مسئولیتی متوجه شرکت فراز اندیشه آرسین نمی باشد . بنابراین کاربر ملزم به حفظ و نگهداری از کلید خصوصی و گواهی امضا خود بوده و چنانچه فایلهای فوق در اختیار اشخاص ثالث قرار گرفته باشد ، مسئولیت این امر با کاربر بوده و حق طرح هرگونه دعوی علیه شرکت فراز اندیشه آرسین را از خود سلب می نماید.
                                    </p>
                                </div>
                            </div>
                            <div className="p-3 bg-light border-top">
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="acceptRulesCheck"
                                        checked={tempRulesAccepted}
                                        onChange={(e) => setTempRulesAccepted(e.target.checked)}
                                    /> 
                                     <label className="form-check-label fw-bold" htmlFor="acceptRulesCheck">
                                        اینجانب قوانین و مقررات را مطالعه کرده و می‌پذیرم.
                                     </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                             <button type="button" className="btn btn-secondary" onClick={() => setStep('rules_summary')}>بازگشت</button>
                             <button type="button" className="btn btn-success" onClick={handleRulesAccept} disabled={!tempRulesAccepted}>تایید و پذیرش</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BranchModal;